import React, { useEffect, useMemo, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ShieldCheck, AlertTriangle, RefreshCw, FileCheck2, PlusCircle, Radar, KeyRound } from "lucide-react";
import {
  COMPOSITION_ORIGIN, RECORDING_ORIGIN, FINAL_AUDIO_AI_PRESENCE, TECHNICAL_AI_USE,
  AI_DEV_HISTORY_TAGS, EVIDENCE_CATEGORY, DECLARANT_CAPACITY, ROUTING_CLASSES,
  ADJUDICATION_STATUSES, SCREENING_PENDING_MESSAGE, humanise,
} from "@/lib/aiProvenance";
import { RoutingClassBadge, SyncGateBadge } from "@/components/AIBadges";
import { useAuth } from "@/lib/auth";

/**
 * AI Provenance panel that lives INSIDE the Track editor (mounted as a new tab).
 *
 * Read-only for catalog_manager on adjudication actions — they can create
 * declarations, submit evidence, request scans, and see status. Only
 * super_admin can finalise or override an assessment.
 */
export default function AIProvenancePanel({ trackId }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "super_admin";
  const isReviewer = user?.role === "gfmp_reviewer" || isAdmin;

  const [summary, setSummary] = useState(null);
  const [history, setHistory] = useState({ assessments: [], declarations: [], scans: [], evidence: [] });
  const [busy, setBusy] = useState(false);

  const [declOpen, setDeclOpen] = useState(false);
  const [evOpen, setEvOpen] = useState(false);
  const [assessOpen, setAssessOpen] = useState(false);
  const [finaliseOpen, setFinaliseOpen] = useState(false);

  const load = async () => {
    if (!trackId) return;
    setBusy(true);
    try {
      const [s, h] = await Promise.all([
        api.get(`/ai-provenance/tracks/${trackId}/summary`),
        api.get(`/ai-provenance/tracks/${trackId}/history`),
      ]);
      setSummary(s.data); setHistory(h.data);
    } catch (e) {
      toast.error(errMsg(e, "Unable to load AI provenance"));
    } finally { setBusy(false); }
  };
  useEffect(() => { load(); }, [trackId]);

  const provider = summary?.provider;
  const assessment = summary?.assessment;

  return (
    <div className="space-y-4" data-testid="ai-provenance-panel">
      {/* Provider status banner */}
      <Card>
        <CardContent className="p-4 flex items-start gap-3">
          <Radar className="w-5 h-5 mt-0.5" style={{ color: provider?.configured ? "hsl(140 55% 32%)" : "hsl(347 72% 22%)" }} />
          <div className="flex-1">
            <div className="text-[10px] uppercase tracking-[0.18em] font-medium text-muted-foreground">Detection provider</div>
            <div className="font-medium" data-testid="ai-provider-status-message">
              {provider?.configured ? `Configured — ${provider.provider_key} / ${provider.provider_model}` : SCREENING_PENDING_MESSAGE}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Current-state header */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="font-display text-lg" data-testid="ai-provenance-current-header">AI Provenance</div>
            <RoutingClassBadge value={summary?.track?.ai_overall_routing_class} testid="ai-current-routing-badge" />
            <SyncGateBadge value={summary?.track?.ai_sync_gate} testid="ai-current-sync-gate" />
            <span className="text-[11px] text-muted-foreground" data-testid="ai-current-adjudication">
              {humanise(summary?.track?.ai_adjudication_status || "UNREVIEWED")}
            </span>
            {summary?.track?.ai_assessment_stale && (
              <span className="text-[11px] px-2 py-0.5 rounded-md border" style={{ background: "hsl(260 20% 94%)", color: "hsl(260 40% 30%)", borderColor: "hsl(260 20% 75%)" }} data-testid="ai-stale-flag">STALE</span>
            )}
            <div className="ml-auto flex gap-2">
              <Button size="sm" variant="outline" onClick={load} disabled={busy} data-testid="ai-provenance-refresh"><RefreshCw className="w-3.5 h-3.5 mr-1" />Refresh</Button>
              <Button size="sm" onClick={() => setDeclOpen(true)} data-testid="ai-provenance-add-declaration"><PlusCircle className="w-3.5 h-3.5 mr-1" />Declaration</Button>
              <Button size="sm" variant="outline" onClick={() => setEvOpen(true)} data-testid="ai-provenance-add-evidence"><FileCheck2 className="w-3.5 h-3.5 mr-1" />Evidence</Button>
              <RunDetectionButton trackId={trackId} onDone={load} />
              <Button size="sm" variant="outline" onClick={() => window.open(`${(process.env.REACT_APP_BACKEND_URL||"").replace(/\/$/, "")}/api/ai-provenance/tracks/${trackId}/report.html?_=${Date.now()}`, "_blank")} data-testid="ai-provenance-report">Report</Button>
              <Button size="sm" variant="outline" onClick={() => setAssessOpen(true)} data-testid="ai-provenance-new-assessment">New Assessment</Button>
              {isAdmin && assessment && (
                <Button size="sm" onClick={() => setFinaliseOpen(true)} style={{ background: "hsl(347 72% 22%)" }} data-testid="ai-provenance-finalise">Finalise</Button>
              )}
            </div>
          </div>

          {/* Split composition / recording */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            <div className="border rounded-md p-3">
              <div className="text-[10px] uppercase tracking-[0.18em] font-medium text-muted-foreground">Composition origin</div>
              <div className="mt-1 font-medium" data-testid="ai-composition-origin">{humanise(assessment?.composition_origin || "UNKNOWN")}</div>
            </div>
            <div className="border rounded-md p-3">
              <div className="text-[10px] uppercase tracking-[0.18em] font-medium text-muted-foreground">Recording origin</div>
              <div className="mt-1 font-medium" data-testid="ai-recording-origin">{humanise(assessment?.recording_origin || "UNKNOWN")}</div>
            </div>
            <div className="border rounded-md p-3">
              <div className="text-[10px] uppercase tracking-[0.18em] font-medium text-muted-foreground">Final-master AI presence</div>
              <div className="mt-1 font-medium">{humanise(assessment?.final_audio_ai_presence || "UNKNOWN")}</div>
            </div>
            <div className="border rounded-md p-3">
              <div className="text-[10px] uppercase tracking-[0.18em] font-medium text-muted-foreground">Technical AI use</div>
              <div className="mt-1 font-medium">{humanise(assessment?.technical_ai_use || "UNKNOWN")}</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* History tabs */}
      <Tabs defaultValue="declarations">
        <TabsList>
          <TabsTrigger value="declarations" data-testid="ai-tab-declarations">Declarations</TabsTrigger>
          <TabsTrigger value="evidence" data-testid="ai-tab-evidence">Evidence</TabsTrigger>
          <TabsTrigger value="scans" data-testid="ai-tab-scans">Scans</TabsTrigger>
          <TabsTrigger value="assessments" data-testid="ai-tab-assessments">Assessments</TabsTrigger>
        </TabsList>
        <TabsContent value="declarations">
          <HistoryList rows={history.declarations} keyFn={(r) => r.id} render={(r) => (
            <div className="flex items-center gap-3">
              <span className="font-medium">{r.declarant_name}</span>
              <span className="text-[12px] text-muted-foreground">{r.declarant_capacity}</span>
              <span className="text-[11px] text-muted-foreground ml-auto">{r.affirmed_at}</span>
            </div>
          )}/>
        </TabsContent>
        <TabsContent value="evidence">
          <HistoryList rows={history.evidence} keyFn={(r) => r.id} render={(r) => (
            <div className="flex items-center gap-3">
              <span className="font-medium">{r.evidence_category}</span>
              <span className="text-[12px] text-muted-foreground">{r.evidence_title}</span>
              <span className="text-[11px] text-muted-foreground ml-auto">{r.review_status}</span>
            </div>
          )}/>
        </TabsContent>
        <TabsContent value="scans">
          <HistoryList rows={history.scans} keyFn={(r) => r.id} render={(r) => (
            <div className="flex items-center gap-3">
              <span className="font-medium">{r.provider_name}</span>
              <span className="text-[12px] text-muted-foreground">{r.status}</span>
              <span className="text-[11px] text-muted-foreground ml-auto">{r.submitted_at}</span>
            </div>
          )}/>
        </TabsContent>
        <TabsContent value="assessments">
          <HistoryList rows={history.assessments} keyFn={(r) => r.id} render={(r) => (
            <div className="flex items-center gap-3">
              <RoutingClassBadge value={r.overall_routing_class} />
              <span className="text-[12px] text-muted-foreground">{r.adjudication_status}</span>
              <span className="text-[11px] text-muted-foreground ml-auto">{r.created_at}</span>
            </div>
          )}/>
        </TabsContent>
      </Tabs>

      <DeclarationDialog open={declOpen} onClose={() => setDeclOpen(false)} trackId={trackId} onDone={load} />
      <EvidenceDialog open={evOpen} onClose={() => setEvOpen(false)} trackId={trackId} assessmentId={assessment?.id} onDone={load} />
      <NewAssessmentDialog open={assessOpen} onClose={() => setAssessOpen(false)} trackId={trackId} declarationId={summary?.current_declaration?.id} onDone={load} />
      {isAdmin && assessment && (
        <FinaliseAssessmentDialog open={finaliseOpen} onClose={() => setFinaliseOpen(false)} assessment={assessment} onDone={load} />
      )}
    </div>
  );
}

function HistoryList({ rows, keyFn, render }) {
  if (!rows || rows.length === 0) return <div className="text-sm text-muted-foreground border border-dashed rounded p-4">No records yet.</div>;
  return (
    <div className="divide-y border rounded">
      {rows.map((r) => (
        <div key={keyFn(r)} className="p-3 text-sm">{render(r)}</div>
      ))}
    </div>
  );
}

function RunDetectionButton({ trackId, onDone }) {
  const [busy, setBusy] = useState(false);
  const fileRef = React.useRef(null);
  async function onPick(e) {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("track_id", trackId);
      fd.append("upload_file", f);
      const r = await api.post("/ai-provenance/scans/run", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const status = r.data?.scan?.status;
      const verdict = r.data?.scan?.primary_verdict;
      if (status === "NOT_CONFIGURED") {
        toast.info("SCAN PENDING — Detection provider not configured.");
      } else if (status === "COMPLETED") {
        toast.success(`Scan complete: ${verdict || "result stored"}.`);
      } else {
        toast.error(r.data?.scan?.error_message_safe || `Scan ${status || "FAILED"}.`);
      }
      onDone?.();
    } catch (err) {
      toast.error(errMsg(err, "Detection scan failed"));
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  }
  return (
    <>
      <input
        ref={fileRef} type="file" hidden
        accept=".wav,.flac,.mp3,.m4a,.aac,.ogg,.opus,.mp4,audio/*"
        onChange={onPick}
        data-testid="ai-run-detection-input"
      />
      <Button size="sm" onClick={() => fileRef.current?.click()} disabled={busy}
              data-testid="ai-provenance-run-detection"
              style={{ background: "hsl(347 72% 22%)" }}>
        <Radar className="w-3.5 h-3.5 mr-1" />
        {busy ? "Scanning…" : "Run AI Detection"}
      </Button>
    </>
  );
}

function DeclarationDialog({ open, onClose, trackId, onDone }) {
  const [form, setForm] = useState({
    declarant_name: "", declarant_email: "", declarant_capacity: "CATALOG_MANAGER",
    ai_use_stages: [], generated_material_remaining: false,
    synthetic_voice_used: false, voice_owner_consent_status: "NOT_APPLICABLE",
    voice_model_licence_status: "NOT_APPLICABLE", affirmed_true: false,
  });
  const canSubmit = form.declarant_name && form.declarant_email && form.affirmed_true;
  async function submit() {
    try {
      await api.post("/ai-provenance/declarations", { ...form, track_id: trackId });
      toast.success("Declaration submitted.");
      onDone?.(); onClose();
    } catch (e) { toast.error(errMsg(e, "Declaration failed")); }
  }
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="ai-declaration-dialog">
        <DialogHeader><DialogTitle>Creator AI Declaration</DialogTitle>
          <DialogDescription>Answer accurately. False or incomplete information may affect clearance and commercial eligibility.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div><Label>Declarant name</Label><Input data-testid="decl-name" value={form.declarant_name} onChange={e => setForm({ ...form, declarant_name: e.target.value })} /></div>
          <div><Label>Declarant email</Label><Input data-testid="decl-email" type="email" value={form.declarant_email} onChange={e => setForm({ ...form, declarant_email: e.target.value })} /></div>
          <div><Label>Capacity</Label>
            <Select value={form.declarant_capacity} onValueChange={(v) => setForm({ ...form, declarant_capacity: v })}>
              <SelectTrigger data-testid="decl-capacity"><SelectValue /></SelectTrigger>
              <SelectContent>{DECLARANT_CAPACITY.map(c => <SelectItem key={c} value={c}>{humanise(c)}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>AI use stages (multi)</Label>
            <div className="flex flex-wrap gap-1.5 mt-1">
              {AI_DEV_HISTORY_TAGS.map(t => {
                const on = form.ai_use_stages.includes(t);
                return <button key={t} type="button" onClick={() => setForm({ ...form, ai_use_stages: on ? form.ai_use_stages.filter(x => x !== t) : [...form.ai_use_stages, t] })}
                  data-testid={`decl-stage-${t.toLowerCase()}`}
                  className={`text-[11px] px-2 py-0.5 rounded border ${on ? "bg-primary/10 border-primary" : "bg-muted/40"}`}>{humanise(t)}</button>;
              })}
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm" data-testid="decl-affirmed-wrap">
            <input type="checkbox" checked={form.affirmed_true} onChange={e => setForm({ ...form, affirmed_true: e.target.checked })} data-testid="decl-affirmed" />
            I affirm the information provided is true and complete to the best of my knowledge.
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={!canSubmit} data-testid="decl-submit" style={{ background: "hsl(347 72% 22%)" }}>Submit declaration</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EvidenceDialog({ open, onClose, trackId, assessmentId, onDone }) {
  const [form, setForm] = useState({ evidence_category: "DAW_SESSION", evidence_title: "", storage_reference: "", relied_upon: true });
  async function submit() {
    try {
      await api.post("/ai-provenance/evidence", { ...form, track_id: trackId, assessment_id: assessmentId });
      toast.success("Evidence added."); onDone?.(); onClose();
    } catch (e) { toast.error(errMsg(e, "Evidence failed")); }
  }
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="ai-evidence-dialog">
        <DialogHeader><DialogTitle>Add AI Provenance Evidence</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Category</Label>
            <Select value={form.evidence_category} onValueChange={(v) => setForm({ ...form, evidence_category: v })}>
              <SelectTrigger data-testid="ev-category"><SelectValue /></SelectTrigger>
              <SelectContent>{EVIDENCE_CATEGORY.map(c => <SelectItem key={c} value={c}>{humanise(c)}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Title / description</Label><Input data-testid="ev-title" value={form.evidence_title} onChange={e => setForm({ ...form, evidence_title: e.target.value })} /></div>
          <div><Label>Internal storage reference (opaque)</Label><Input data-testid="ev-ref" value={form.storage_reference} onChange={e => setForm({ ...form, storage_reference: e.target.value })} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={!form.evidence_title.trim()} data-testid="ev-submit" style={{ background: "hsl(347 72% 22%)" }}>Add</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function NewAssessmentDialog({ open, onClose, trackId, declarationId, onDone }) {
  const [form, setForm] = useState({
    composition_origin: "UNKNOWN", recording_origin: "UNKNOWN",
    final_audio_ai_presence: "UNKNOWN", technical_ai_use: "UNKNOWN",
    ai_development_history: [], evaluated_file_hash_sha256: "", evaluated_file_name: "",
  });
  async function submit() {
    try {
      await api.post("/ai-provenance/assessments", { ...form, track_id: trackId, declaration_id: declarationId || null });
      toast.success("Assessment created."); onDone?.(); onClose();
    } catch (e) { toast.error(errMsg(e, "Assessment failed")); }
  }
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="ai-new-assessment-dialog">
        <DialogHeader><DialogTitle>New AI Provenance Assessment</DialogTitle>
          <DialogDescription>The assessment tracks composition and recording provenance separately. Verified NO_AI_HISTORY_VERIFIED requires super_admin finalisation with complete evidence.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div><Label>Composition origin</Label>
            <Select value={form.composition_origin} onValueChange={(v) => setForm({ ...form, composition_origin: v })}>
              <SelectTrigger data-testid="assess-composition"><SelectValue /></SelectTrigger>
              <SelectContent>{COMPOSITION_ORIGIN.map(v => <SelectItem key={v} value={v}>{humanise(v)}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Recording origin</Label>
            <Select value={form.recording_origin} onValueChange={(v) => setForm({ ...form, recording_origin: v })}>
              <SelectTrigger data-testid="assess-recording"><SelectValue /></SelectTrigger>
              <SelectContent>{RECORDING_ORIGIN.map(v => <SelectItem key={v} value={v}>{humanise(v)}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Final-master AI presence</Label>
            <Select value={form.final_audio_ai_presence} onValueChange={(v) => setForm({ ...form, final_audio_ai_presence: v })}>
              <SelectTrigger data-testid="assess-final-presence"><SelectValue /></SelectTrigger>
              <SelectContent>{FINAL_AUDIO_AI_PRESENCE.map(v => <SelectItem key={v} value={v}>{humanise(v)}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Technical AI use</Label>
            <Select value={form.technical_ai_use} onValueChange={(v) => setForm({ ...form, technical_ai_use: v })}>
              <SelectTrigger data-testid="assess-technical"><SelectValue /></SelectTrigger>
              <SelectContent>{TECHNICAL_AI_USE.map(v => <SelectItem key={v} value={v}>{humanise(v)}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="md:col-span-2"><Label>Evaluated file SHA-256 (paste; not uploaded from here)</Label>
            <Input data-testid="assess-hash" value={form.evaluated_file_hash_sha256} onChange={e => setForm({ ...form, evaluated_file_hash_sha256: e.target.value })} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} data-testid="assess-submit" style={{ background: "hsl(347 72% 22%)" }}>Create assessment</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function FinaliseAssessmentDialog({ open, onClose, assessment, onDone }) {
  const [target, setTarget] = useState(assessment?.overall_routing_class || "UNKNOWN");
  const [notes, setNotes] = useState("");
  const [override, setOverride] = useState(false);
  const [ovConf, setOvConf] = useState("");
  const [ovReason, setOvReason] = useState("");
  const canSubmit = !override || (ovConf.trim() === "OVERRIDE AI CLASSIFICATION" && ovReason.trim().length >= 20);
  async function submit() {
    try {
      await api.post(`/ai-provenance/assessments/${assessment.id}/finalise`, {
        target_routing_class: target, target_adjudication_status: "VERIFIED",
        review_notes: notes, override, override_confirmation: ovConf, override_reason: ovReason,
      });
      toast.success("Assessment finalised."); onDone?.(); onClose();
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (typeof d === "object" && d?.missing) toast.error(`Cannot certify: ${d.missing.join(" \u2022 ")}`);
      else toast.error(errMsg(e, "Finalise failed"));
    }
  }
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="ai-finalise-dialog">
        <DialogHeader><DialogTitle>Finalise Assessment</DialogTitle>
          <DialogDescription>Verified NO_AI_HISTORY_VERIFIED requires two evidence categories including one production-source category, current declaration, and (when configured) a completed provider scan.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div><Label>Target routing class</Label>
            <Select value={target} onValueChange={setTarget}>
              <SelectTrigger data-testid="fin-target"><SelectValue /></SelectTrigger>
              <SelectContent>{ROUTING_CLASSES.map(v => <SelectItem key={v} value={v}>{humanise(v)}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Notes</Label><Textarea data-testid="fin-notes" value={notes} onChange={e => setNotes(e.target.value)} rows={3} /></div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={override} onChange={e => setOverride(e.target.checked)} data-testid="fin-override" />
            Override missing evidence
          </label>
          {override && (
            <>
              <div><Label>Type OVERRIDE AI CLASSIFICATION to confirm</Label><Input data-testid="fin-ov-conf" value={ovConf} onChange={e => setOvConf(e.target.value)} /></div>
              <div><Label>Override reason (min 20 chars)</Label><Textarea data-testid="fin-ov-reason" value={ovReason} onChange={e => setOvReason(e.target.value)} rows={3} /></div>
            </>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={!canSubmit} data-testid="fin-submit" style={{ background: "hsl(347 72% 22%)" }}>Finalise</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
